import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type QaRun } from './api';

/**
 * Freshness for the account state a *verification run* changes, not the browser.
 *
 * The app-wide query defaults (60s stale time, no refetch on focus) assume data
 * only moves in response to a mutation this tab performed, which already
 * invalidates it. Usage breaks that assumption in the same way the credentials
 * list does: hours are reported from the customer's own machine, minutes after
 * the dashboard was opened, and nothing in this tab is told.
 *
 * The result was a dashboard that never moved. A ten-minute run charged its
 * hours on the server, the row went green in the database, and "Hours
 * remaining" kept showing the figure it had cached before the run started —
 * indistinguishable, to the person reading it, from metering being broken.
 *
 * Two triggers, because either alone leaves a real case stale: coming back from
 * the terminal is usually a focus event, but not when the dashboard sits on a
 * second monitor and never lost focus at all.
 */
const agentReported = {
  refetchOnWindowFocus: true,
  staleTime: 0,
} as const;

/** Poll cadence while a run is actually executing. */
const POLL_MS = 15_000;

/**
 * How long a run may sit in `running` before we stop expecting it to move.
 *
 * A run whose agent was killed outright — power loss, SIGKILL, a closed laptop —
 * never sends its close, and the server has no sweeper to reclaim it, so the row
 * stays `running` permanently. Without this bound those rows would hold the
 * dashboard in a polling loop for the life of the tab, re-requesting every
 * fifteen seconds forever over a run that ended days ago.
 */
const ABANDONED_AFTER_MS = 60 * 60 * 1000;

/**
 * Parse a timestamp as the UTC the server meant.
 *
 * The API serialises naive UTC with no zone suffix (`2026-08-29T08:04:20`), and
 * `new Date()` reads a bare date-time as *local* time. Left alone, every
 * comparison here would be wrong by the viewer's UTC offset — which for a
 * one-hour threshold is the difference between "running" and "abandoned" for
 * most of the world.
 */
export function parseServerTime(iso: string): Date {
  return new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
}

/** Whether any run is executing now, and so may still change these figures. */
export function runInFlight(runs: QaRun[] | undefined, now: number = Date.now()): boolean {
  return (runs ?? []).some(
    (run) =>
      run.status === 'running' &&
      now - parseServerTime(run.started_at).getTime() < ABANDONED_AFTER_MS,
  );
}

/** The run list alone, for pages that do not show the hours balance. */
export function useLiveRuns() {
  return useQuery({
    queryKey: ['runs'],
    queryFn: api.getRuns,
    ...agentReported,

    // Poll only while something is actually executing. Once every run has
    // settled the polling stops on its own, so an idle dashboard is as quiet
    // as it was before.
    refetchInterval: (query) => (runInFlight(query.state.data) ? POLL_MS : false),
  });
}

/**
 * The subscription and the run list, kept current against out-of-band changes.
 *
 * Shared by every page that shows remaining hours so they cannot drift apart on
 * how fresh they are — one page correcting itself while another shows a stale
 * balance is worse than both being stale.
 */
export function useLiveUsage() {
  const queryClient = useQueryClient();
  const runs = useLiveRuns();
  const inFlight = runInFlight(runs.data);

  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: api.getSubscription,
    ...agentReported,
    refetchInterval: inFlight ? POLL_MS : false,
  });

  // The hours are charged by the *same* call that flips the run out of
  // `running`, so the poll that observes the run finishing is racing the
  // subscription's own refetch — and it wins about half the time, which would
  // freeze the balance one run behind until something else refetched it.
  // Settle it explicitly on the falling edge instead of hoping the intervals
  // interleave favourably.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !inFlight) {
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
    }
    wasInFlight.current = inFlight;
  }, [inFlight, queryClient]);

  return { subscription, runs };
}
